import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../utils/api'

export const useTaskComments = (taskId, options = {}) => {
  const [comments, setComments] = useState([])
  const [attachments, setAttachments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  const { user } = useAuth()
  
  const {
    autoFetch = true
  } = options

  // ดึงความเห็นทั้งหมด
  const fetchComments = async () => {
    if (!taskId) return
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get(`/api/tasks/${taskId}/comments`)
      
      if (response.data.success) {
        setComments(response.data.data)
      } else {
        throw new Error(response.data.message || 'ไม่สามารถดึงความเห็นได้')
      }
    } catch (err) {
      console.error('Error fetching comments:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงความเห็น')
    } finally {
      setLoading(false)
    }
  }

  // สร้างความเห็นใหม่
  const createComment = async (commentData) => {
    if (!taskId) return { success: false, message: 'ไม่พบรหัสงานย่อย' }
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.post(`/api/tasks/${taskId}/comments`, commentData)
      
      if (response.data.success) {
        // เพิ่มความเห็นใหม่เข้าไปใน state
        setComments(prev => [...prev, response.data.data])
        
        return { success: true, data: response.data.data }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถสร้างความเห็นได้')
      }
    } catch (err) {
      console.error('Error creating comment:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการสร้างความเห็น'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // อัปเดตความเห็น
  const updateComment = async (commentId, commentData) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.put(`/api/comments/${commentId}`, commentData)
      
      if (response.data.success) {
        // อัปเดตใน comments array
        setComments(prev => prev.map(comment => 
          comment.id === commentId ? response.data.data : comment
        ))
        
        return { success: true, data: response.data.data }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถอัปเดตความเห็นได้')
      }
    } catch (err) {
      console.error('Error updating comment:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการอัปเดตความเห็น'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // ลบความเห็น
  const deleteComment = async (commentId) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.delete(`/api/comments/${commentId}`)
      
      if (response.data.success) {
        // ลบออกจาก comments array
        setComments(prev => prev.filter(comment => comment.id !== commentId))
        
        return { success: true }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถลบความเห็นได้')
      }
    } catch (err) {
      console.error('Error deleting comment:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการลบความเห็น'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // ดึงไฟล์แนบทั้งหมด
  const fetchAttachments = async () => {
    if (!taskId) return
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get(`/api/tasks/${taskId}/attachments`)
      
      if (response.data.success) {
        setAttachments(response.data.data.attachments)
      } else {
        throw new Error(response.data.message || 'ไม่สามารถดึงไฟล์แนบได้')
      }
    } catch (err) {
      console.error('Error fetching attachments:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงไฟล์แนบ')
    } finally {
      setLoading(false)
    }
  }

  // อัปโหลดไฟล์แนบ
  const uploadAttachment = async (file, options = {}) => {
    if (!taskId) return { success: false, message: 'ไม่พบรหัสงานย่อย' }
    
    try {
      setLoading(true)
      setError(null)
      
      const formData = new FormData()
      formData.append('attachment', file)
      
      // เพิ่ม options
      if (options.description) {
        formData.append('description', options.description)
      }
      if (options.comment_id) {
        formData.append('comment_id', options.comment_id)
      }
      if (options.is_public !== undefined) {
        formData.append('is_public', options.is_public.toString())
      }
      
      const response = await api.post(`/api/tasks/${taskId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      if (response.data.success) {
        // เพิ่มไฟล์แนบใหม่เข้าไปใน state
        setAttachments(prev => [...prev, response.data.data])
        
        return { success: true, data: response.data.data }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถอัปโหลดไฟล์แนบได้')
      }
    } catch (err) {
      console.error('Error uploading attachment:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการอัปโหลดไฟล์แนบ'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // ลบไฟล์แนบ
  const deleteAttachment = async (attachmentId) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.delete(`/api/attachments/${attachmentId}`)
      
      if (response.data.success) {
        // ลบออกจาก attachments array
        setAttachments(prev => prev.filter(attachment => attachment.id !== attachmentId))
        
        return { success: true }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถลบไฟล์แนบได้')
      }
    } catch (err) {
      console.error('Error deleting attachment:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการลบไฟล์แนบ'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // รีเฟรชข้อมูล
  const refreshComments = () => {
    fetchComments()
    fetchAttachments()
  }

  // ดึงข้อมูลอัตโนมัติเมื่อ taskId เปลี่ยน
  useEffect(() => {
    if (taskId && autoFetch) {
      fetchComments()
      fetchAttachments()
    }
  }, [taskId, autoFetch])

  return {
    // Data
    comments,
    attachments,
    loading,
    error,
    
    // Methods
    fetchComments,
    createComment,
    updateComment,
    deleteComment,
    fetchAttachments,
    uploadAttachment,
    deleteAttachment,
    refreshComments,
    
    // Helpers
    canCreateComments: user?.permissions?.task_comments?.create === true,
    canReadComments: user?.permissions?.task_comments?.read === true,
    canUpdateComments: user?.permissions?.task_comments?.update === true,
    canDeleteComments: user?.permissions?.task_comments?.delete === true,
    canCreateAttachments: user?.permissions?.task_attachments?.create === true,
    canReadAttachments: user?.permissions?.task_attachments?.read === true,
    canDeleteAttachments: user?.permissions?.task_attachments?.delete === true
  }
}

export default useTaskComments